import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthModal from '../ui/AuthModal';
import DailyDiscoveryService from '../../services/dailyDiscovery';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import PageHeader from '../ui/PageHeader';

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 769 : false
  );

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 769);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return isDesktop;
};

const Feature = ({ id, image, title, description, onClick, disabled = false }) => {
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleClick = () => {
    if (!disabled) {
      onClick();
    }
  };

  return (
    <>
      <button
        type="button"
        className={`feature-section ${disabled ? 'disabled' : ''}`}
        onClick={handleClick}
      >
        <div className="feature-image" aria-hidden="true">
          <img src={image} alt="" />
        </div>
        <div className="feature-scrim" aria-hidden="true" />
        <div className="feature-content">
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
      </button>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLogin={() => {}}
      />
    </>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const isDesktop = useIsDesktop();
  const [discoveryTitle, setDiscoveryTitle] = useState('');

  useEffect(() => {
    if (isDesktop) return undefined;

    const loadDiscoveryTitle = async () => {
      try {
        const discovery = await DailyDiscoveryService.getTodayDiscovery(language);
        if (discovery && discovery.title) {
          setDiscoveryTitle(discovery.title);
        } else {
          setDiscoveryTitle(t('home.dailyDiscovery.title'));
        }
      } catch (error) {
        console.log('[Home] Could not load discovery title');
        setDiscoveryTitle(t('home.dailyDiscovery.title'));
      }
    };
    loadDiscoveryTitle();
  }, [language, t, isDesktop]);

  const features = [
    {
      id: 'similar',
      image: `${process.env.PUBLIC_URL}/img/cerca.jpg`,
      title: t('home.similarArtists.title'),
      description: t('home.similarArtists.description'),
      onClick: () => navigate('/similar', {
        state: {
          type: 'similar'
        }
      })
    },
    {
      id: 'daily-discovery',
      image: `${process.env.PUBLIC_URL}/img/daily.jpg`,
      title: isDesktop
        ? t('home.approfondimenti.title')
        : (discoveryTitle || t('home.dailyDiscovery.title')),
      description: isDesktop
        ? t('home.approfondimenti.description')
        : t('home.dailyDiscovery.description'),
      onClick: () => {
        if (isDesktop) {
          navigate('/daily-discovery', {
            state: {
              type: 'daily-discovery',
              showWall: true,
              timestamp: Date.now()
            }
          });
          return;
        }
        navigate('/daily-discovery', {
          state: {
            type: 'daily-discovery',
            loadLatest: true,
            timestamp: Date.now()
          }
        });
      }
    },
    {
      id: 'random',
      image: `${process.env.PUBLIC_URL}/img/random.jpg`,
      title: t('home.randomArtists.title'),
      description: t('home.randomArtists.description'),
      onClick: () => navigate('/random', {
        state: {
          type: 'random'
        }
      })
    }
  ];

  return (
    <div className={`home-page ${isDesktop ? 'home-page--desktop' : ''}`}>
      {isDesktop && (
        <div className="home-page__intro">
          <PageHeader
            title={t('home.claim')}
            subtitle={t('home.intro')}
          />
        </div>
      )}
      <div className="features-container">
        {features.map((feature) => (
          <Feature
            key={feature.id}
            {...feature}
          />
        ))}
      </div>
    </div>
  );
};

export default Home;
